import React, { useEffect, useRef, useState } from "react";
import { ChevronDown, LogOut, PencilRuler, ShieldCheck, Sprout, User } from "lucide-react";
import { useNavigate } from "react-router-dom";

import { useAuth } from "../contexts/AuthContext";
import { PLATFORM_CITY_LABEL, PLATFORM_NAME } from "@/lib/polenica";

export default function Header() {
  const { user, isGuest, logout } = useAuth();
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef(null);

  const isAdmin = user?.role === "superadmin" || user?.rol === "ADMINISTRADOR";

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!menuRef.current?.contains(event.target)) {
        setMenuOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const handleLogout = async () => {
    await logout();
    navigate("/", { replace: true });
  };

  return (
    <header className="platform-top-chrome sticky top-0 z-50 border-b border-border backdrop-blur-xl">
      <div className="mx-auto max-w-7xl px-4 py-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between gap-4">
          <div className="flex items-center gap-4">
            <button type="button" onClick={() => navigate("/inicio")} className="flex items-center gap-3 text-left">
              <Sprout className="h-8 w-8 text-primary" />
              <div>
                <h1 className="font-heading text-lg font-semibold text-primary">{PLATFORM_NAME}</h1>
                <p className="text-xs text-mutedForeground">{PLATFORM_CITY_LABEL}</p>
              </div>
            </button>

            <button
              type="button"
              onClick={() => navigate("/quienes-somos")}
              className="hidden rounded-full border border-primary/12 bg-[#F3F8EE]/95 px-4 py-2 text-sm font-semibold text-primary shadow-sm transition hover:bg-[#E7F0E0] lg:inline-flex"
            >
              Quiénes somos
            </button>
          </div>

          {(user || isGuest) ? (
            <div ref={menuRef} className="relative">
              <button
                type="button"
                onClick={() => setMenuOpen((current) => !current)}
                className="flex items-center gap-3 rounded-2xl border border-primary/10 bg-[#F3F8EE] px-4 py-3 text-left shadow-sm"
              >
                <div className="flex h-10 w-10 items-center justify-center rounded-2xl bg-primary/10 text-primary">
                  <User className="h-4 w-4" />
                </div>
                <div>
                  <p className="text-sm font-semibold text-primary">{user ? `${user.nombres} ${user.apellidos}` : "Invitado"}</p>
                  <p className="text-xs text-mutedForeground">{user ? "Menu principal" : "Exploracion libre"}</p>
                </div>
                {isAdmin ? <ShieldCheck className="h-4 w-4 text-accent" /> : null}
                <ChevronDown className={`h-4 w-4 text-primary transition-transform ${menuOpen ? "rotate-180" : ""}`} />
              </button>

              {menuOpen ? (
                <div className="absolute right-0 top-[calc(100%+12px)] min-w-[230px] rounded-[24px] border border-primary/10 bg-[#F4F8F0] p-2 shadow-[0_22px_44px_rgba(25,45,33,0.14)]">
                  {user ? (
                    <>
                      <button type="button" onClick={() => { navigate("/perfil"); setMenuOpen(false); }} className="flex w-full items-center gap-3 rounded-2xl px-4 py-3 text-left text-sm font-semibold text-primary transition hover:bg-[#E7F0E0]">
                        <User className="h-4 w-4" />
                        Mi perfil
                      </button>
                      {isAdmin ? (
                        <>
                          <button type="button" onClick={() => { navigate("/admin"); setMenuOpen(false); }} className="flex w-full items-center gap-3 rounded-2xl px-4 py-3 text-left text-sm font-semibold text-primary transition hover:bg-[#E7F0E0]">
                            <ShieldCheck className="h-4 w-4" />
                            Panel admin
                          </button>
                          <button type="button" onClick={() => { navigate("/admin/contenido"); setMenuOpen(false); }} className="flex w-full items-center gap-3 rounded-2xl px-4 py-3 text-left text-sm font-semibold text-primary transition hover:bg-[#E7F0E0]">
                            <PencilRuler className="h-4 w-4" />
                            Editor visual
                          </button>
                        </>
                      ) : null}
                      <button type="button" onClick={handleLogout} className="flex w-full items-center gap-3 rounded-2xl px-4 py-3 text-left text-sm font-semibold text-[#8A3D22] transition hover:bg-[#FFF1EC]">
                        <LogOut className="h-4 w-4" />
                        Salir
                      </button>
                    </>
                  ) : (
                    <button type="button" onClick={() => { navigate("/login"); setMenuOpen(false); }} className="flex w-full items-center gap-3 rounded-2xl px-4 py-3 text-left text-sm font-semibold text-primary transition hover:bg-[#E7F0E0]">
                      <User className="h-4 w-4" />
                      Entrar a Polénica
                    </button>
                  )}
                </div>
              ) : null}
            </div>
          ) : (
            <div className="hidden items-center gap-3 lg:flex">
              <button
                type="button"
                onClick={() => navigate("/quienes-somos")}
                className="rounded-full border border-primary/12 bg-[#F3F8EE]/95 px-4 py-2 text-sm font-semibold text-primary shadow-sm transition hover:bg-[#E7F0E0]"
              >
                Quiénes somos
              </button>
              <button
                type="button"
                onClick={() => navigate("/login")}
                className="rounded-full bg-primary px-4 py-2 text-sm font-semibold text-primaryForeground shadow-sm transition hover:bg-[#45694A]"
              >
                Entrar
              </button>
            </div>
          )}
        </div>
      </div>
    </header>
  );
}
